/**
 * Helpers for the on-dashboard editor: where a new widget goes, how big it
 * starts, and what each kind of widget is called.
 */

/** A KPI widget: one KPI, as a small tile. */
export const KPI_TILE = 'tile';

/** The saved settings of a single-KPI widget. The name is kept for the editor's labels; the widget itself shows the KPI's current name. */
export const kpiTileConfig = (kpi) => ({ tile: true, kpiIds: [kpi.id], kpiName: kpi.name });

/** Rows are shorter on a phone, so a tile takes one more of them to fit its value and change. */
const KPI_TILE_PHONE_ROWS = 3;

/** Where a KPI tile sits at each grid size: small wherever there is room, full width on a phone. */
export const kpiTilePosition = (slot) => ({
  lg: slot, md: slot, sm: slot, xs: { x: 0, y: slot.y, w: 12, h: KPI_TILE_PHONE_ROWS },
});

/** Default size (grid units on a 12-column grid) for a new widget. */
export function defaultSize(kind, widgetType = null) {
  if (kind === 'metric') {
    if (widgetType === 'table' || widgetType === 'service_status') return { w: 4, h: 4 };
    if (widgetType === 'line_chart' || widgetType === 'area_chart' || widgetType === 'chart') return { w: 6, h: 4 };
    return { w: 3, h: 3 };
  }
  if (kind === 'kpi') return { w: 3, h: 2 };
  if (kind === 'statement') return { w: 6, h: 7 };
  return { w: 6, h: 4 };
}

/** Grid row height at a window width. */
export function rowHeightFor(windowWidth) {
  if (windowWidth < 768) return 60;
  if (windowWidth < 996) return 70;
  if (windowWidth < 1200) return 80;
  if (windowWidth < 1920) return 90;
  return 100;
}

/** The size in pixels a new widget takes up on a dashboard grid of this width. */
export function widgetPixelSize(kind, widgetType, gridWidth, windowWidth) {
  const { w, h: rows } = defaultSize(kind, widgetType);
  // Every KPI widget is a tile.
  const tile = kind === 'kpi' || widgetType === KPI_TILE;
  const h = tile && gridWidth < 768 ? KPI_TILE_PHONE_ROWS : rows;
  const margin = windowWidth < 768 ? 8 : 10;
  // Below these grid widths a new widget is saved full width, apart from a KPI tile on a mid-sized grid.
  const fullWidth = gridWidth < 768 || (gridWidth < 996 && !tile);
  const cols = gridWidth >= 1920 ? 18 : 12;
  const cell = (gridWidth - margin * (cols - 1)) / cols;
  const columns = fullWidth || w >= 12 ? cols : w;
  return {
    width: Math.round(columns * cell + (columns - 1) * margin),
    height: h * rowHeightFor(windowWidth) + (h - 1) * margin,
  };
}

export const PREVIEW_WIDGET_ID = 'preview';

/** The widget a library entry becomes when added, for showing it before it exists. */
export function previewOf(action) {
  const id = PREVIEW_WIDGET_ID;
  switch (action.kind) {
    case 'metric': {
      const m = action.metric;
      return {
        kind: 'metric',
        widgetType: m.widget_type,
        widget: { id, widget_kind: 'metric', metric_key: m.metric_key, widget_type: m.widget_type, display_name: m.display_name || m.metric_key, metric_config: m.config || {}, config: {} },
      };
    }
    case 'report':
      return { kind: 'dataset', widget: { id, widget_kind: 'dataset', dataset_name: action.report.datasetName, report: action.report, config: {} } };
    case 'library': {
      const l = action.item;
      return {
        kind: 'dataset',
        widget: {
          id,
          widget_kind: 'dataset',
          dataset_name: l.datasetName,
          report: { name: l.title, cubeQuery: l.cubeQuery, display: { displayType: l.displayType, inheritDateRange: true }, refreshMinutes: l.refreshMinutes || 30 },
          config: {},
        },
      };
    }
    case 'statement':
      return { kind: 'statement', widget: { id, widget_kind: 'statement', dataset_name: action.layout.datasetName, config: { datasetName: action.layout.datasetName, layoutKey: action.layout.key } } };
    case 'kpi':
      return { kind: 'kpi', widgetType: KPI_TILE, widget: { id, widget_kind: 'kpi', config: kpiTileConfig(action.kpi) } };
    default:
      throw new Error(`Unknown widget: ${action.kind}`);
  }
}

const overlaps = (a, b) => !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);

/**
 * First gap that fits w×h, scanning top to bottom then left to right, so new
 * widgets fill holes before going to the bottom.
 * @param {{x,y,w,h}[]} items existing layout items
 */
export function findFreeSlot(items, w, h, cols = 12) {
  const width = Math.min(w, cols);
  const bottom = items.reduce((max, it) => Math.max(max, it.y + it.h), 0);
  for (let y = 0; y <= bottom; y++) {
    for (let x = 0; x + width <= cols; x++) {
      const candidate = { x, y, w: width, h };
      if (!items.some((it) => overlaps(candidate, it))) return candidate;
    }
  }
  return { x: 0, y: bottom, w: width, h };
}

const WIDESCREEN_COLS = 18;
export const GRID_BREAKPOINTS = { xl: 1920, lg: 1200, md: 996, sm: 768, xs: 480 };

/** The grid's layout at a width, chosen the way the dashboard's grid chooses it. */
export function breakpointFor(gridWidth) {
  return ['xl', 'lg', 'md', 'sm'].find((name) => gridWidth >= GRID_BREAKPOINTS[name]) || 'xs';
}

export const gridColumns = (breakpoint) => (breakpoint === 'xl' ? WIDESCREEN_COLS : 12);

/** React Grid Layout items for one widget at every breakpoint, with size limits by widget type. */
export function layoutItemsFor(widget, widescreenSlot = null) {
  const pos = widget.position || {};
  const hasBreakpoints = pos.xl !== undefined || pos.lg !== undefined;
  const out = {};
  ['xl', 'lg', 'md', 'sm', 'xs'].forEach((breakpoint) => {
    const bpPos = hasBreakpoints
      ? (pos[breakpoint] || (breakpoint === 'xl' && widescreenSlot) || { x: 0, y: 0, w: 4, h: 3 })
      : { x: pos.x || 0, y: pos.y || 0, w: pos.w || 4, h: pos.h || 3 };

    let constraints = { minW: 2, minH: 2 };
    let width = bpPos.w || 4;
    let height = bpPos.h || 3;

    if (widget.widget_type === 'gauge') {
      if (breakpoint === 'xl') constraints = { minW: 2, minH: 2, maxW: 8, maxH: 4 };
      else if (breakpoint === 'lg') constraints = { minW: 1, minH: 2, maxW: 6, maxH: 4 };
      else if (breakpoint === 'md') constraints = { minW: 3, minH: 2, maxW: 8, maxH: 4 };
      else if (breakpoint === 'sm') { constraints = { minW: 4, minH: 3, maxH: 4 }; width = Math.max(width, 4); height = Math.max(height, 3); }
      else { constraints = { minW: 6, minH: 3, maxH: 4 }; width = Math.max(width, 6); height = Math.max(height, 3); }
    } else if (widget.widget_type === 'count') {
      if (breakpoint === 'xl') constraints = { minW: 2, minH: 2, maxW: 8 };
      else if (breakpoint === 'lg') constraints = { minW: 1, minH: 2, maxW: 6 };
      else if (breakpoint === 'md') constraints = { minW: 3, minH: 2, maxW: 8 };
      else if (breakpoint === 'sm' || breakpoint === 'xs') { constraints = { minW: 6, minH: 2 }; width = Math.max(width, 6); }
    }

    out[breakpoint] = { i: widget.id.toString(), x: bpPos.x || 0, y: bpPos.y || 0, w: width, h: height, ...constraints };
  });
  return out;
}

/**
 * Which dashboards to repeat after the last one so that the screen is always
 * full when the page reaches the end of the loop.
 * @param {number[]} heights each dashboard's height in pixels
 * @returns {number[]} indexes into the list, in order
 */
export function sectionsToRepeat(heights, screenHeight) {
  const repeats = [];
  if (heights.length === 0 || !heights.some((h) => h > 0)) return repeats;
  let covered = 0;
  for (let n = 0; covered < screenHeight || repeats.length === 0; n += 1) {
    const index = n % heights.length;
    repeats.push(index);
    covered += heights[index];
  }
  return repeats;
}

/**
 * Lays a run of same-sized widgets out in even rows that fill the widescreen
 * grid: as few rows as their width allows, shared out equally, so there are no
 * holes at the end of a row.
 */
function fillRows(run, w, h, startY, slots) {
  const fullWidth = w >= 12;
  const fit = fullWidth ? 1 : Math.max(1, Math.floor(WIDESCREEN_COLS / w));
  const rows = Math.ceil(run.length / fit);
  const perRow = Math.ceil(run.length / rows);
  let y = startY;
  for (let start = 0; start < run.length; start += perRow, y += h) {
    const row = run.slice(start, start + perRow);
    row.forEach((widget, n) => {
      const x = Math.round((n * WIDESCREEN_COLS) / row.length);
      const end = Math.round(((n + 1) * WIDESCREEN_COLS) / row.length);
      slots.set(widget.id, { x, y, w: end - x, h });
    });
  }
  return y;
}

/**
 * Places widgets that have no saved widescreen position, in the order they read
 * on a normal screen. With nothing saved, widgets of the same size share even
 * rows across the wider grid and a full-width widget stays full width. When some
 * are already arranged, the rest go into the first free space around them.
 * @param {Object[]} widgets dashboard widgets
 * @param {{x,y,w,h}[]} taken other items already on the widescreen grid
 * @returns {Map<number, {x,y,w,h}>} position by widget id
 */
export function widescreenSlots(widgets, taken = []) {
  const placed = [...taken, ...widgets.filter((w) => w.position?.xl).map((w) => w.position.xl)];
  const slots = new Map();
  const todo = widgets
    .filter((w) => w.position?.lg && !w.position.xl)
    .sort((a, b) => a.position.lg.y - b.position.lg.y || a.position.lg.x - b.position.lg.x);
  if (placed.length === 0) {
    let y = 0;
    for (let start = 0; start < todo.length;) {
      const { w, h } = todo[start].position.lg;
      let end = start + 1;
      while (end < todo.length && todo[end].position.lg.w === w && todo[end].position.lg.h === h) end += 1;
      y = fillRows(todo.slice(start, end), w, h, y, slots);
      start = end;
    }
    return slots;
  }
  for (const widget of todo) {
    const { w, h } = widget.position.lg;
    const slot = findFreeSlot(placed, w >= 12 ? WIDESCREEN_COLS : w, h, WIDESCREEN_COLS);
    placed.push(slot);
    slots.set(widget.id, slot);
  }
  return slots;
}

/** What a widget is, for toolbars and confirmations. */
export function widgetLabel(widget) {
  const cfg = widget.config || {};
  if (cfg.title) return cfg.title;
  if (widget.widget_kind === 'kpi') return cfg.kpiName || 'KPI';
  if (widget.widget_kind === 'statement') return cfg.layoutKey ? cfg.layoutKey.replace(/_/g, ' ') : 'Statement';
  if (widget.widget_kind === 'dataset') return widget.report?.name || widget.dataset_name || 'Report';
  return widget.display_name || widget.metric_key || 'Widget';
}

export const KIND_LABEL = {
  metric: 'Live metric',
  dataset: 'Report',
  kpi: 'KPI',
  statement: 'Financial statement',
};

export function widgetKindLabel(widget) {
  if (widget.widget_kind === 'kpi' && widget.config?.tile) return 'KPI';
  return KIND_LABEL[widget.widget_kind || 'metric'] || 'Widget';
}
